import { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';

import { useSYS5010_R01Store } from './Store_SYS5010_R01';
import type { SYS5010_R01Filter } from './Store_SYS5010_R01';
import {
	downloadExcel_SYS5010_R01,
	getSYS5010_R01AllCount,
	getSYS5010_R01List,
} from './Api_SYS5010_R01';
import { SYS5010_R01Dto } from './Dto_SYS5010_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS5010_R01Dto> | null>;
	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
}

export const Content_SYS5010_R01 = (props: Props) => {
	const { gridRef: _gridRef, tabList: _tabList, setMessage } = props;

	const {
		SYS5010_R01Filter,
		SYS5010_R01PastFilter,
		setSYS5010_R01PastFilter,
		setSYS5010_R01List,
		setAllCount,
		initData,
		setScrollTop,
	} = useSYS5010_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth('SYS5010_R01');

	const getParams = (filter: SYS5010_R01Filter): Partial<SYS5010_R01Filter> => ({
		dateRange: filter.dateRange,
		userId: filter.userId,
	});

	const renewalAllCount = useCallback(async () => {
		try {
			const res = await getSYS5010_R01AllCount(getParams(SYS5010_R01Filter));
			setAllCount(res.data.data);
			return res.data.data;
		} catch {
			showError('접속 기록 조회 중 오류가 발생했습니다.');
			return 0;
		}
	}, [SYS5010_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		try {
			const total = await renewalAllCount();
			const res = await getSYS5010_R01List('search', getParams(SYS5010_R01Filter));
			if (res.data.successFlag) {
				setSYS5010_R01List(res.data.data);
				setSYS5010_R01PastFilter(SYS5010_R01Filter);
				ShowSuccessFetch(setMessage, `접속 기록 조회가 완료되었습니다. (총 ${res.data.data.length}/${total}건)`);
			} else {
				showError('접속 기록 조회 중 오류가 발생했습니다.');
			}
		} catch {
			showError('접속 기록 조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [SYS5010_R01Filter, setSYS5010_R01List, setSYS5010_R01PastFilter, renewalAllCount, setLoadingSpinner, setScrollTop]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const res = await downloadExcel_SYS5010_R01('search', getParams(SYS5010_R01PastFilter));
			if (res.data.successFlag) {
				downloadSettingExcel({ responseData: res.data.data, fileName: '접속 기록.xlsx' });
				ShowSuccessFetch(setMessage, '접속 기록 다운로드가 완료되었습니다.');
			} else {
				showWarning('접속 기록 다운로드 중 오류가 발생했습니다.');
			}
		} catch {
			showError('접속 기록 다운로드 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [SYS5010_R01PastFilter, setLoadingSpinner, setMessage]);

	const initContainerData = useCallback(() => {
		initData();
		setMessage('초기화되었습니다.');
	}, [initData, setMessage]);

	return { fetchData, handleExcelClick, initContainerData };
};

export default Content_SYS5010_R01;
